import React from "react";

import {
  faCheckCircle,
  faCircleNotch,
  faExclamationCircle,
  faExclamationTriangle,
  faXmark,
} from "@fortawesome/pro-regular-svg-icons";
import toast, { ToastBar } from "react-hot-toast";
import { twMerge } from "tailwind-merge";

import { Icon, IconButton } from "src/design-system/Icon";
import { NotificationVariant, Props } from "src/design-system/Toast/constants";
import { useTrackNotification } from "src/design-system/Toast/utils";
import { assertUnreachable } from "src/utils/typeUtils";

export const RESET_TOASTBAR_STYLES: React.CSSProperties = {
  padding: 0,
  boxShadow: "unset",
  borderRadius: 0,
  background: "transparent",
};

export const getIcon = (variant: NotificationVariant): React.ReactNode => {
  switch (variant) {
    case "default":
      return null;

    case "success":
      return <Icon icon={faCheckCircle} size="md" variant="success" />;

    case "error":
      return <Icon icon={faExclamationCircle} size="md" variant="error" />;
    case "warning":
      return <Icon icon={faExclamationTriangle} size="md" variant="warning" />;
    case "loading":
      return <Icon icon={faCircleNotch} size="md" variant="active" spin />;

    default:
      assertUnreachable(variant);
      return null;
  }
};

/**
 * Toast component
 * NOTE: Avoid using this component directly, use helpers from utils
 * Figma: https://www.figma.com/design/BO9DWO8PKNGNSI3HhpSobA/Design-System-v2?node-id=9503-2702&node-type=text&m=dev
 */
export const Toast: React.FC<Props> = ({
  dismissible = true,
  variant,
  title,
  description,
  toastInstance,
  actionText,
  onActionClick,
  wordBreakAll,
}) => {
  useTrackNotification({ variant, title, description });
  return (
    <ToastBar style={RESET_TOASTBAR_STYLES} toast={toastInstance}>
      {() => (
        <div
          className="flex max-h-96 w-[365px] gap-x-2 rounded-2xl bg-white p-5 shadow-lg"
          data-loc={`${variant}-notification`}
          data-record="true"
        >
          {getIcon(variant)}
          <div className="flex flex-grow flex-col gap-y-2">
            <div className="flex items-start justify-between">
              <p
                className={twMerge(
                  "font-inter-semibold-16px text-gray-800",
                  wordBreakAll && "break-all",
                )}
                data-record="true"
              >
                {title}
              </p>
              {dismissible && (
                <IconButton
                  dataLoc="notification-close"
                  icon={faXmark}
                  size="md"
                  onClick={() => toast.dismiss(toastInstance.id)}
                />
              )}
            </div>

            {description && (
              <div
                className="decideScrollbar font-inter-normal-13px text-gray-500"
                data-record="true"
              >
                {description}
              </div>
            )}

            {actionText && (
              <button
                className="text-left font-inter-semibold-13px text-gray-800 hover:underline"
                onClick={onActionClick}
              >
                {actionText}
              </button>
            )}
          </div>
        </div>
      )}
    </ToastBar>
  );
};
